import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const features = [
    {
      icon: '👥',
      title: 'Employee Management',
      description: 'Comprehensive employee directory with profiles, roles, and department organization.'
    },
    {
      icon: '🔐',
      title: 'Secure Access',
      description: 'Enterprise-grade security with Google SSO integration and role-based permissions.'
    },
    {
      icon: '📊',
      title: 'Insights',
      description: 'Real-time dashboards and reports for data-driven HR decisions.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="landing-logo">
            <div className="logo-icon">S</div>
            <span>Sodel</span>
          </div>
          <div className="landing-nav-links">
            <a href="#features">Features</a>
            <a href="#about">About</a>
            <button className="btn-nav" onClick={handleGetStarted}>
              {user ? 'Dashboard' : 'Sign In'}
            </button>
          </div>
        </nav>

        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              Sodel Solutions HRMS
            </div>
            <h1 className="hero-title">
              HR Management
              <span className="gradient-text"> for Agile Teams</span>
            </h1>
            <p className="hero-subtitle">
              Streamline your human resource operations with our sleek, intuitive platform. 
              Built by IIT Bombay alumni, trusted by 120+ technologists.
            </p>
            <div className="hero-cta">
              <button className="btn-primary btn-large" onClick={handleGetStarted}>
                {user ? 'Go to Dashboard' : 'Get Started'}
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
              <a href="#features" className="btn-text">
                Learn more
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">120+</span>
                <span className="stat-label">Team Members</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">2007</span>
                <span className="stat-label">Founded</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">17+</span>
                <span className="stat-label">Years Experience</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="glass-card main-card">
              <div className="card-header-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div className="card-content">
                <div className="avatar-stack">
                  <div className="avatar">JD</div>
                  <div className="avatar">AS</div>
                  <div className="avatar">MK</div>
                  <div className="avatar-more">+42</div>
                </div>
                <p className="card-text">Team Overview</p>
              </div>
            </div>
            <div className="glass-card floating-card card-1">
              <div className="mini-chart">
                <div className="bar" style={{ height: '40%' }}></div>
                <div className="bar" style={{ height: '70%' }}></div>
                <div className="bar" style={{ height: '50%' }}></div>
                <div className="bar active" style={{ height: '90%' }}></div>
              </div>
              <p>Leave Analytics</p>
            </div>
            <div className="glass-card floating-card card-2">
              <div className="check-icon">✓</div>
              <p>Approved</p>
            </div>
            <div className="glow-orb orb-1"></div>
            <div className="glow-orb orb-2"></div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Features</span>
            <h2 className="section-title">Everything you need to<br />manage your team</h2>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-content">
            <div className="about-text">
              <span className="section-badge">About Sodel</span>
              <h2>Your Technology Partner<br />for Digital Innovation</h2>
              <p>
                Founded in 2007 by IIT Bombay alumni, Sodel Solutions brings almost two decades 
                of software expertise across multiple technology fields. Our agile engineering team 
                delivers projects within our fail-safe agile framework ensuring predictability 
                and profitability.
              </p>
              <div className="about-links">
                <a href="https://www.sodelsolutions.com" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                  Visit sodelsolutions.com
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="about-services">
              <h4>Our Services</h4>
              <ul className="services-list">
                <li>Product Development</li>
                <li>Commercial Application Development</li>
                <li>ERP Applications</li>
                <li>Data Analysis and Visualization</li>
                <li>Server Optimization</li>
                <li>Solution Architecture</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-card">
            <div className="cta-content">
              <h2>Ready to streamline your HR?</h2>
              <p>Join the team at Sodel Solutions and experience HR management.</p>
            </div>
            <button className="btn-primary btn-large" onClick={handleGetStarted}>
              {user ? 'Go to Dashboard' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="landing-logo">
                <div className="logo-icon">S</div>
                <span>Sodel</span>
              </div>
              <p>Comprehensive Digital Engineering | IT Services | Expert IT Consulting</p>
            </div>
            <div className="footer-contact">
              <h4>Contact</h4>
              <p>marketing@sodelsolutions.com</p>
              <p>022-41239390</p>
              <p>Sodel Solutions Pvt. Ltd., #205, Bldg 3, Sec 3, MBP, Mahape, Navi Mumbai 400710</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Sodel Solutions Pvt. Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
